import React from 'react';

/**
 * CarouselCardPreview - faithful WYSIWYG render of a single auto-carousel card.
 *
 * Mirrors the ReportLab layout system in /app/backend/auto_carousel_service.py
 * Layouts supported: cover, insight, bullets, quote, stat, two_col, cta
 *
 * Native canvas size = 1080x1350. We scale to caller-provided `width`.
 */

// Editorial palette - must mirror auto_carousel_service.py exactly
const PAPER = '#FAFAF7';
const INK = '#0B1220';
const INK_SOFT = '#1F2937';
const MUTED = '#475569';
const HAIRLINE = '#E5E7EB';
const CARD_DARK = '#0B1220';
const COVER_SUBTITLE_GRAY = '#9CA3AF';

// Font-family CSS strings keyed by backend font value. Web fonts are loaded
// via the Google Fonts link in /app/frontend/public/index.html.
const FONT_FAMILY_CSS = {
  inter:    "'Inter', 'Helvetica Neue', Arial, sans-serif",
  opensans: "'Open Sans', 'Helvetica Neue', Arial, sans-serif",
  playfair: "'Playfair Display', 'Source Serif Pro', Georgia, serif",
};

function resolveFontStack(font) {
  return FONT_FAMILY_CSS[font] || FONT_FAMILY_CSS.inter;
}

function getHighlightRanges(line, highlights) {
  if (!highlights || !highlights.length) return [];
  const ll = line.toLowerCase();
  const ranges = [];
  for (const h of highlights) {
    if (!h) continue;
    const needle = String(h).toLowerCase();
    let start = 0;
    // Cap each highlight at 20 occurrences for safety
    for (let n = 0; n < 20; n += 1) {
      const idx = ll.indexOf(needle, start);
      if (idx < 0) break;
      ranges.push([idx, idx + needle.length]);
      start = idx + needle.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  // Merge overlaps
  const merged = [];
  for (const [a, b] of ranges) {
    if (merged.length && a <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(b, merged[merged.length - 1][1]);
    } else {
      merged.push([a, b]);
    }
  }
  return merged;
}

function HighlightedText({ text, highlights, accent, defaultColor, style }) {
  if (!text) return null;
  const ranges = getHighlightRanges(text, highlights);
  if (!ranges.length) {
    return <span style={{ color: defaultColor, ...style }}>{text}</span>;
  }
  const segments = [];
  let cursor = 0;
  ranges.forEach(([a, b], i) => {
    if (a > cursor) {
      segments.push({ text: text.slice(cursor, a), highlight: false, key: `n${i}` });
    }
    segments.push({ text: text.slice(a, b), highlight: true, key: `h${i}` });
    cursor = b;
  });
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlight: false, key: 'tail' });
  }
  return (
    <span style={style}>
      {segments.map((s) =>
        s.highlight ? (
          <span
            key={s.key}
            style={{
              color: accent,
              borderBottom: `0.12em solid ${accent}`,
              paddingBottom: '0.04em',
            }}
          >
            {s.text}
          </span>
        ) : (
          <span key={s.key} style={{ color: defaultColor }}>{s.text}</span>
        ),
      )}
    </span>
  );
}

function BrandMark({ brandLabel, brandLogo, inverted, maxHeight = 28 }) {
  if (brandLogo) {
    return (
      <img
        src={brandLogo}
        alt="brand logo"
        style={{
          maxHeight,
          maxWidth: 140,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }
  // No logo AND no brand label -> render nothing (avoid forcing a hardcoded brand)
  if (!brandLabel) return null;
  return (
    <span
      style={{
        fontFamily: 'inherit',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.06em',
        color: inverted ? '#FFFFFF' : INK,
        textTransform: 'uppercase',
      }}
    >
      {brandLabel}
    </span>
  );
}

/**
 * Header strip for non-cover cards (matches _draw_card_chrome).
 */
function CardHeader({ brandLabel, brandLogo, accent, deckTitle }) {
  return (
    <div style={{ position: 'relative', padding: '20px 28px 14px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!brandLogo && (
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: accent, display: 'inline-block',
            }}
          />
        )}
        <BrandMark brandLabel={brandLabel} brandLogo={brandLogo} maxHeight={22} />
        <div style={{ flex: 1 }} />
        {deckTitle && (
          <span
            style={{
              fontFamily: 'inherit',
              fontSize: 9,
              letterSpacing: '0.08em',
              color: MUTED,
              textTransform: 'uppercase',
              textAlign: 'right',
              maxWidth: '50%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {deckTitle}
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          borderBottom: `1px solid ${HAIRLINE}`,
        }}
      />
    </div>
  );
}

/**
 * Footer strip for non-cover cards.
 */
function CardFooter({ brandLabel, idx, total }) {
  return (
    <div
      style={{
        padding: '10px 28px 14px 28px',
        borderTop: `1px solid ${HAIRLINE}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontFamily: 'inherit',
          fontSize: 9,
          color: MUTED,
        }}
      >
        {brandLabel || ''}
      </span>
      <span
        style={{
          fontFamily: 'inherit',
          fontSize: 10,
          fontWeight: 700,
          color: INK,
        }}
      >
        {String(idx).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- Layouts

function CoverLayout({ card, deckSubtitle, accent, brandLabel, brandLogo, total }) {
  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: CARD_DARK,
        color: '#FFFFFF',
        height: '100%',
        padding: '24px 28px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
      }}
    >
      <BrandMark brandLabel={brandLabel} brandLogo={brandLogo} inverted maxHeight={26} />
      <div
        style={{
          width: 56,
          height: 3,
          backgroundColor: accent,
          marginTop: 50,
        }}
      />
      {card.kicker && (
        <div
          style={{
            color: accent,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.10em',
            marginTop: 16,
            textTransform: 'uppercase',
          }}
        >
          {card.kicker}
        </div>
      )}
      <h1
        style={{
          fontSize: 32,
          lineHeight: 1.08,
          fontWeight: 800,
          marginTop: 14,
          letterSpacing: '-0.01em',
          color: '#FFFFFF',
        }}
      >
        <HighlightedText
          text={card.headline || ''}
          highlights={card.highlights || []}
          accent={accent}
          defaultColor="#FFFFFF"
        />
      </h1>
      <div style={{ flex: 1 }} />
      {deckSubtitle && (
        <div
          style={{
            fontSize: 12,
            color: COVER_SUBTITLE_GRAY,
            lineHeight: 1.4,
            marginBottom: 12,
            maxWidth: '90%',
          }}
        >
          {deckSubtitle}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: COVER_SUBTITLE_GRAY,
            letterSpacing: '0.1em',
          }}
        >
          SWIPE →
        </span>
        <span
          style={{
            fontSize: 10,
            color: COVER_SUBTITLE_GRAY,
            fontWeight: 700,
          }}
        >
          01 / {String(total).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
}

function ContentShell({ children, brandLabel, brandLogo, accent, deckTitle, idx, total }) {
  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: PAPER,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
      }}
    >
      <CardHeader
        brandLabel={brandLabel}
        brandLogo={brandLogo}
        accent={accent}
        deckTitle={deckTitle}
      />
      <div style={{ flex: 1, padding: '12px 28px 14px 28px', overflow: 'hidden' }}>
        {children}
      </div>
      <CardFooter brandLabel={brandLabel} idx={idx} total={total} />
    </div>
  );
}

function KickerBlock({ kicker, accent }) {
  if (!kicker) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          color: accent,
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          width: 22,
          height: 2,
          backgroundColor: accent,
          marginTop: 4,
        }}
      />
    </div>
  );
}

function Headline({ text, highlights, accent, size = 22 }) {
  if (!text) return null;
  return (
    <h2
      style={{
        fontSize: size,
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '-0.01em',
        margin: 0,
      }}
    >
      <HighlightedText
        text={text}
        highlights={highlights}
        accent={accent}
        defaultColor={INK}
      />
    </h2>
  );
}

function InsightLayout({ card, accent }) {
  return (
    <>
      <KickerBlock kicker={card.kicker} accent={accent} />
      <Headline
        text={card.headline}
        highlights={card.highlights || []}
        accent={accent}
        size={22}
      />
      {card.body && (
        <p
          style={{
            fontSize: 12,
            lineHeight: 1.55,
            color: INK_SOFT,
            marginTop: 14,
            whiteSpace: 'pre-wrap',
          }}
        >
          <HighlightedText
            text={card.body}
            highlights={card.highlights || []}
            accent={accent}
            defaultColor={INK_SOFT}
          />
        </p>
      )}
    </>
  );
}

function BulletsLayout({ card, accent }) {
  const bullets = card.bullets || [];
  const highs = card.bullet_highlights || [];
  return (
    <>
      <KickerBlock kicker={card.kicker} accent={accent} />
      <Headline
        text={card.headline}
        highlights={card.highlights || []}
        accent={accent}
        size={20}
      />
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bullets.slice(0, 5).map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: accent,
                color: '#FFFFFF',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              {i + 1}
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: INK_SOFT }}>
              <HighlightedText
                text={b}
                highlights={highs[i] ? [highs[i]] : []}
                accent={accent}
                defaultColor={INK_SOFT}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function QuoteLayout({ card, accent }) {
  return (
    <>
      <div
        style={{
          color: accent,
          fontSize: 56,
          lineHeight: 0.8,
          fontWeight: 800,
          marginBottom: 0,
        }}
      >
        “
      </div>
      <div
        style={{
          fontSize: 20,
          lineHeight: 1.2,
          fontWeight: 700,
          color: INK,
          marginTop: 6,
        }}
      >
        {card.quote || card.headline}
      </div>
      {card.attribution && (
        <div
          style={{
            fontSize: 11,
            color: MUTED,
            fontStyle: 'italic',
            marginTop: 16,
          }}
        >
          — {card.attribution}
        </div>
      )}
    </>
  );
}

function StatLayout({ card, accent }) {
  return (
    <>
      <KickerBlock kicker={card.kicker} accent={accent} />
      <div
        style={{
          color: accent,
          fontSize: 92,
          fontWeight: 800,
          lineHeight: 0.95,
          letterSpacing: '-0.02em',
        }}
      >
        {card.stat || ''}
      </div>
      {card.stat_label && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: INK,
            marginTop: 6,
            lineHeight: 1.25,
          }}
        >
          {card.stat_label}
        </div>
      )}
      {card.body && (
        <p
          style={{
            fontSize: 11,
            color: MUTED,
            lineHeight: 1.5,
            marginTop: 12,
          }}
        >
          <HighlightedText
            text={card.body}
            highlights={card.highlights || []}
            accent={accent}
            defaultColor={MUTED}
          />
        </p>
      )}
    </>
  );
}

function TwoColLayout({ card, accent }) {
  return (
    <>
      <KickerBlock kicker={card.kicker} accent={accent} />
      <Headline
        text={card.headline}
        highlights={card.highlights || []}
        accent={accent}
        size={18}
      />
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              color: accent,
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {card.two_col_left || ''}
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.4, color: INK_SOFT }}>
            {card.two_col_left_body}
          </div>
        </div>
        <div style={{ backgroundColor: HAIRLINE }} />
        <div>
          <div
            style={{
              color: accent,
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {card.two_col_right || ''}
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.4, color: INK_SOFT }}>
            {card.two_col_right_body}
          </div>
        </div>
      </div>
    </>
  );
}

function CtaLayout({ card, accent }) {
  return (
    <>
      <KickerBlock kicker={card.kicker} accent={accent} />
      <Headline
        text={card.headline}
        highlights={card.highlights || []}
        accent={accent}
        size={22}
      />
      {card.body && (
        <p
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            color: INK_SOFT,
            marginTop: 14,
          }}
        >
          <HighlightedText
            text={card.body}
            highlights={card.highlights || []}
            accent={accent}
            defaultColor={INK_SOFT}
          />
        </p>
      )}
      <div style={{ flex: 1 }} />
      <div
        style={{
          marginTop: 16,
          display: 'inline-block',
          backgroundColor: accent,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.08em',
          padding: '10px 18px',
          borderRadius: 999,
          alignSelf: 'flex-start',
        }}
      >
        {(card.cta || 'JOIN THE CONVERSATION').toUpperCase()} →
      </div>
    </>
  );
}

// ----------------------------------------------------------------- Main

export default function CarouselCardPreview({
  card,
  idx,
  total,
  deckTitle,
  deckSubtitle,
  brandLabel,
  brandLogo,
  accent,
  font = 'inter',
  width = 320,
}) {
  if (!card) return null;
  const layout = card.layout || 'insight';
  const height = Math.round(width * (5 / 4));

  const containerStyle = {
    width,
    height,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0 14px 36px rgba(11,18,32,0.15)',
    backgroundColor: PAPER,
    fontFamily: resolveFontStack(font),
  };

  if (layout === 'cover') {
    return (
      <div style={containerStyle}>
        <CoverLayout
          card={card}
          deckSubtitle={deckSubtitle}
          accent={accent}
          brandLabel={brandLabel}
          brandLogo={brandLogo}
          total={total}
        />
      </div>
    );
  }

  let inner = null;
  switch (layout) {
    case 'bullets': inner = <BulletsLayout card={card} accent={accent} />; break;
    case 'quote':   inner = <QuoteLayout   card={card} accent={accent} />; break;
    case 'stat':    inner = <StatLayout    card={card} accent={accent} />; break;
    case 'two_col': inner = <TwoColLayout  card={card} accent={accent} />; break;
    case 'cta':     inner = <CtaLayout     card={card} accent={accent} />; break;
    default:        inner = <InsightLayout card={card} accent={accent} />;
  }

  return (
    <div style={containerStyle}>
      <ContentShell
        brandLabel={brandLabel}
        brandLogo={brandLogo}
        accent={accent}
        deckTitle={deckTitle}
        idx={idx}
        total={total}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {inner}
        </div>
      </ContentShell>
    </div>
  );
}
